//
// Where the progress store gets its two inputs from.
//
// Push alone would be less reliable than the polling it replaces: a client that
// misses an event never learns that it missed one. So the socket delivers what
// it can, and the snapshot fills the gaps — on mount, and after every connect,
// which is exactly when events were lost.
//
// Split off from progressStore because both halves reach outside the browser:
// the socket module reads import.meta, which the store's tests cannot load.
//

import TaskModel from '@client/models/task'
import { connectSocketOnce, getSocket } from '@client/socket/socket'
import { applyEvent, applySnapshot, getProgressTask, taskFromSnapshot } from './progressStore'

const taskModel = new TaskModel()

/** Reads the tenant's tasks and merges them into what is already known. */
export async function refreshProgress(): Promise<void> {
    try {
        const res: any = await taskModel.list()
        if (!Array.isArray(res?.data)) return

        applySnapshot(res.data)
    } catch {
        // A snapshot that fails is not worth reporting: the socket keeps
        // feeding the display, and the next connect tries again.
    }
}

/**
 * Asks about a single id the store does not know.
 *
 * For the case the list cannot cover: a task that started while the connection
 * was down, or one older than the fifty most recent. The answer a caller needs
 * is 'missing' — that is what lets a view stop waiting for a task that will
 * never report.
 */
export async function fetchProgressTask(taskId: string): Promise<'known' | 'missing' | 'unclear'> {
    if (!taskId) return 'unclear'
    if (getProgressTask(taskId)) return 'known'

    try {
        const res: any = await taskModel.read(taskId)
        if (res?.status === 404) return 'missing'
        if (!taskFromSnapshot(res?.data)) return 'unclear'

        applySnapshot([res.data])

        return 'known'
    } catch {
        return 'unclear'
    }
}

let watchers = 0
let detach: (() => void) | null = null

const onProgressChanged = (payload: any) => applyEvent(payload)

// Every reconnect, and the first connect too: what happened while the socket
// was down is precisely what no event could deliver.
const onConnect = () => {
    void refreshProgress()
}

/**
 * Starts watching and returns the matching stop.
 *
 * Reference-counted: several progress displays can be open at once — an import
 * dialog while an export runs — and only the last one to close takes the
 * listeners with it.
 */
export function startProgressWatch(): () => void {
    watchers += 1

    if (watchers === 1) {
        const socket = connectSocketOnce()
        socket.on('progress:changed', onProgressChanged)
        socket.on('connect', onConnect)

        detach = () => {
            const s = getSocket()
            s.off('progress:changed', onProgressChanged)
            s.off('connect', onConnect)
        }

        // The connect handler only fires on a connect still to come; an
        // already-connected socket would leave the display empty until the
        // first event arrives.
        void refreshProgress()
    }

    return () => {
        watchers -= 1
        if (watchers > 0) return

        watchers = 0
        detach?.()
        detach = null
    }
}
